import {
  FuzzySearchResourceType,
  teamMemberSearchableResourceTypes,
} from "@repo/app-types/entities/search";
import {
  isTeammateATeamMember,
  type TeammateId,
} from "@repo/app-types/entities/teammate";
import { useAppSelector } from "../../hooks/hook.js";
import { useTeammateIdParam } from "../../hooks/useTeammateIdParam.js";
import { selectTeammate } from "../teammate/teammateSlice.js";

const searchContexts = {
  go: {
    resourceTypes: undefined,
    placeholder: "Search...",
    supportsTranscriptKeywordSearch: false,
  },
  recruiting: {
    resourceTypes: [FuzzySearchResourceType.Interview],
    placeholder: "Search candidates...",
    supportsTranscriptKeywordSearch: false,
  },
  training: {
    resourceTypes: [FuzzySearchResourceType.Interview],
    placeholder: "Search training participants...",
    supportsTranscriptKeywordSearch: false,
  },
  team_performance: {
    resourceTypes: [
      FuzzySearchResourceType.TeammateWithGradings,
      FuzzySearchResourceType.TeammateAccountGrading,
    ],
    placeholder: "Try ‘customer asked to think it over’ or ‘callback’",
    supportsTranscriptKeywordSearch: true,
  },
} as const;

export type SearchContextType = keyof typeof searchContexts;

export interface SearchDialogContext {
  placeholder: string;
  resourceTypes: readonly FuzzySearchResourceType[] | undefined;
  isTrainingSearchContext: boolean;
  showTranscriptKeywordSearch: boolean;
  isTeamPerformanceSearchContext: boolean;
  /**
   * Sends the "Search all calls" entry point to this teammate's calls page
   * instead of the playbook-wide one: the teammate whose page the search was
   * opened from, or, for a team member, themselves.
   */
  transcriptSearchTeammateId: TeammateId | undefined;
}

// Resolves the search placeholder + resource filter for a context. The
// team-member restriction (they may only search calls) is repeated here so the
// modal shows the right sections; the API enforces it regardless of what the
// client asks for.
export function useSearchDialogContext(
  context: SearchContextType,
): SearchDialogContext {
  const teammate = useAppSelector(selectTeammate);
  const isTeamMember = teammate && isTeammateATeamMember(teammate);
  const routeTeammateId = useTeammateIdParam();
  const searchContext = searchContexts[context];

  return {
    placeholder: searchContext.placeholder,
    resourceTypes: isTeamMember
      ? teamMemberSearchableResourceTypes
      : searchContext.resourceTypes,
    isTrainingSearchContext: context === "training",
    isTeamPerformanceSearchContext: context === "team_performance",
    showTranscriptKeywordSearch: searchContext.supportsTranscriptKeywordSearch,
    // Searching from a teammate's page searches that teammate's calls. A team
    // member is held to their own regardless: another teammate's calls page
    // redirects them away (see `TeamMemberTeammateAccessGuard`), which would
    // drop the query on the way.
    transcriptSearchTeammateId: isTeamMember ? teammate.id : routeTeammateId,
  };
}
